import { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ROUTES } from "../config/constants";
import PrivateRoute from "./PrivateRoute";
import Layout from "../views/Layout";
import LoadingComponent from "../components/LoadingComponent";

// Lazy load all views
const Login = lazy(() => import("../views/Login"));
const Register = lazy(() => import("../views/Register"));
const ResetPassword = lazy(() => import("../views/ResetPassword"));
const OTPVerification = lazy(() => import("../views/OTPVerification"));
const Overview = lazy(() => import("../views/Overview"));
const NoraChatPage = lazy(() => import("../pages/NoraChatPage"));
const LegalResearch = lazy(() => import("../views/LegalResearch"));
// const CaseFinder = lazy(() => import("../views/CaseFinder"));

// Document Analyzer
const DocumentLayout = lazy(() => import("../views/DocumentAnalyzer/DocumentLayout"));
const UploadLayout = lazy(() => import("../views/DocumentAnalyzer/UploadLayout"));
const UploadPage = lazy(() => import("../views/DocumentAnalyzer/pages/UploadPage"));
const OptionsPage = lazy(() => import("../views/DocumentAnalyzer/pages/OptionsPage"));
const SummaryPage = lazy(() => import("../views/DocumentAnalyzer/pages/SummaryPage"));
const ChatPage = lazy(() => import("../views/DocumentAnalyzer/pages/ChatPage"));

// const FastDraft = lazy(() => import("../views/FastDraft"));
// const SubscribeCases = lazy(() => import("../views/SubscribeCases"));
// const CaseDetailPage = lazy(() => import("../views/SubscribeCases/CaseDetailPage"));
// const MyCalendar = lazy(() => import("../views/MyCalendar"));

// Document Review imports
// const DocumentReviewLayout = lazy(() => import("../views/DocumentReview/DocumentReviewLayout"));
// const DocumentReviewUploadLayout = lazy(() => import("../views/DocumentReview/UploadLayout"));
// const DocumentReviewUploadPage = lazy(() => import("../views/DocumentReview/pages/UploadPage"));
// const DocumentReviewOptionsPage = lazy(() => import("../views/DocumentReview/pages/OptionsPage"));
// const DocumentReviewQuestionsPage = lazy(() => import("../views/DocumentReview/pages/QuestionsPage"));
// const DocumentReviewResultsPage = lazy(() => import("../views/DocumentReview/pages/ResultsPage"));

// const JudgementFinder = lazy(() => import("../views/JudgementFinder"));
// const CustomDraft = lazy(() => import("../views/CustomDraft"));
// const DraftPro = lazy(() => import("../views/DraftPro"));
const LegalVault = lazy(() => import("../views/LegalVault"));
const NeurologicalResearch = lazy(() => import("../views/NeurologicalResearch"));
const PromptGuide = lazy(() => import("../views/PromptGuide"));
const UserProfileCompletion = lazy(() => import("../views/UserProfileCompletion"));
const AuthCallback = lazy(() => import("../views/AuthCallback"));
// const ChooseDraftingMethod = lazy(() => import("../views/DraftifySelection/ChooseDraftingMethod"));
const WalletPage = lazy(() => import("../views/wallet/WalletPageWrapper"));
const Profile = lazy(() => import("../views/Profile"));
const NotificationsPage = lazy(() => import("../views/NotificationsPage.jsx"));
const PrivacyPolicy = lazy(() => import("../views/HelpAndSupport/PrivacyPolicy"));
const TermsOfService = lazy(() => import("../views/HelpAndSupport/TermsOfService"));
const Disclaimer = lazy(() => import("../views/HelpAndSupport/Disclaimer"));

// Event Extraction
const EventLayout = lazy(() => import("../views/EventExtraction/EventLayout"));
const EventUploadPage = lazy(() => import("../views/EventExtraction/pages/UploadPage"));
const EventPage = lazy(() => import("../views/EventExtraction/pages/EventPage"));

const ChangelogPage = lazy(() => import("../views/ChangelogPage"));

// Legal Notice
const LegalNoticeLayout = lazy(() => import('../views/LegalNotice/LegalNoticeLayout'));
const LegalNoticeUploadPage = lazy(() => import('../views/LegalNotice/pages/UploadPage'));
const LegalNoticePage = lazy(() => import('../views/LegalNotice/pages/NoticePage'));

// const LinkedInPostGenerator = lazy(() => import("../views/LinkedInPostGenerator"));
const FeedbackPage = lazy(() => import("../views/FeedbackPage"));
const SettingsPage = lazy(() => import("../views/Settings"));

// Scanned Copy
const ScannedCopyLayout = lazy(() => import("../views/ScannedCopy/ScannedCopyLayout"));
const ScannedCopyUploadPage = lazy(() => import("../views/ScannedCopy/pages/UploadPage"));
const ScannedCopyOptionsPage = lazy(() => import("../views/ScannedCopy/pages/OptionsPage"));
const ScannedCopyPreviewPage = lazy(() => import("../views/ScannedCopy/pages/PreviewPage"));

// Translator
const TranslatorLayout = lazy(() => import("../views/Translator/TranslatorLayout"));
const TranslatorUploadPage = lazy(() => import("../views/Translator/pages/UploadPage"));
const TranslatorOptionsPage = lazy(() => import("../views/Translator/pages/OptionsPage"));
const TranslatorPreviewPage = lazy(() => import("../views/Translator/pages/PreviewPage"));

// Audio Transcription
// const AudioTranscriptionLayout = lazy(() => import("../views/AudioTranscription/AudioTranscriptionLayout"));
// const AudioTranscriptionUploadPage = lazy(() => import("../views/AudioTranscription/pages/UploadPage"));
// const AudioTranscriptionLanguageSelectionPage = lazy(() => import("../views/AudioTranscription/pages/LanguageSelectionPage"));
// const AudioTranscriptionTranscriptPage = lazy(() => import("../views/AudioTranscription/pages/TranscriptPage"));

const WaitingPage = lazy(() => import("../views/WaitingPage/index.jsx"));
const BannedPage = lazy(() => import("../views/BannedPage/index.jsx"));
const PricingPage = lazy(() => import("../views/Pricing"));
const OrganizationDashboard = lazy(() => import("../views/OrganizationDashboard"));
const OrganizationInvitation = lazy(() => import("../views/OrganizationInvitation"));
const NotFound = lazy(() => import("../views/NotFound"));

// PDF Tools
const PdfToolsLayout = lazy(() => import("../views/PdfTools/PdfToolsLayout"));
const PdfPaginate = lazy(() => import("../views/PdfPaginate/PdfPaginate"));
const PdfErase = lazy(() => import("../views/PdfErase/PdfErase"));
const PdfMerge = lazy(() => import("../views/PdfMerge/PdfMerge"));
const PdfRemovePages = lazy(() => import("../views/PdfRemovePages/PdfRemovePages"));

const AppRoutes = ({ toggleFeedbackModal, isFeedbackModalOpen }) => {
  const { isAuthenticated } = useSelector((state) => state.auth);

  return (
    <Suspense fallback={<LoadingComponent />}>
      <Routes>
      {/* Public routes */}
      <Route
        path={ROUTES.LOGIN}
        element={
          isAuthenticated ? <Navigate to={ROUTES.HOME} /> : <Login /> // Redirect to HOME ('/')
        }
      />
      <Route
        path={ROUTES.REGISTER}
        element={isAuthenticated ? <Navigate to={ROUTES.HOME} /> : <Register />}
      />
      <Route path="/reset-password/:resettoken" element={<ResetPassword />} />{" "}
      {/* Public route for password reset */}
      <Route
        path={ROUTES.OTP_VERIFICATION}
        element={<OTPVerification />}
      />{" "}
      {/* Public route for OTP verification */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path={ROUTES.ORG_INVITATION} element={<OrganizationInvitation />} />
      <Route
        path={ROUTES.HOME}
        element={
          <Layout
            toggleFeedbackModal={toggleFeedbackModal}
            isFeedbackModalOpen={isFeedbackModalOpen}
          />
        }
      >
        <Route
          path={ROUTES.NEUROLOGICAL_RESEARCH}
          element={<NeurologicalResearch />}
        />
        <Route
          path={`${ROUTES.NEUROLOGICAL_RESEARCH}/prompt-guide`}
          element={<PromptGuide />}
        />
        <Route
          path={ROUTES.WAITING}
          element={<WaitingPage />}
        />
        <Route
          path={ROUTES.BANNED}
          element={<BannedPage />}
        />
        <Route path={ROUTES.TERMS_OF_SERVICE} element={<TermsOfService />} />
        <Route path={ROUTES.PRIVACY_POLICY} element={<PrivacyPolicy />} />
        <Route path={ROUTES.CHANGELOG} element={<ChangelogPage />} />
        <Route path={ROUTES.PRICING} element={<PricingPage />} />
      </Route>
      <Route element={<PrivateRoute />}>
        <Route
          path={ROUTES.HOME}
          element={
            <Layout
              toggleFeedbackModal={toggleFeedbackModal}
              isFeedbackModalOpen={isFeedbackModalOpen}
            />
          }
        >
          {" "}
          {/* Nora Chat as default home page */}
          <Route index element={<NoraChatPage />} />
          <Route path={ROUTES.CHATINTERFACE} element={<NoraChatPage />} />
          <Route path={ROUTES.CHAT_WITH_ID} element={<NoraChatPage />} />
          <Route path={ROUTES.NEUROLOGICAL_RESEARCH} element={<NeurologicalResearch />} />
          <Route path="prompt-guide" element={<PromptGuide />} />
          <Route path={ROUTES.PROFILE} element={<Profile />} />
          <Route path={ROUTES.OVERVIEW} element={<Overview />} />
          <Route path={ROUTES.LEGAL_RESEARCH} element={<LegalResearch />} />
          {/* <Route path={ROUTES.CASE_FINDER} element={<CaseFinder />} /> */}
          <Route path={ROUTES.DOCUMENT_ANALYZER} element={<UploadLayout />}>
            <Route index element={<UploadPage />} />
          </Route>
          <Route path={`${ROUTES.DOCUMENT_ANALYZER}/:id`} element={<DocumentLayout />}>
            <Route index element={<Navigate to="options" replace />} />
            <Route path="options" element={<OptionsPage />} />
            <Route path="summary/:lang?" element={<SummaryPage />} />
            <Route path="chat" element={<ChatPage />} />
          </Route>
          <Route path={ROUTES.EVENT_EXTRACTION} element={<EventLayout />}>
            <Route index element={<EventUploadPage />} />
            <Route path=":id" element={<EventPage />} />
          </Route>
          {/* <Route path={ROUTES.LEGAL_NOTICE} element={<LegalNoticeLayout />}>
            <Route index element={<LegalNoticeUploadPage />} />
            <Route path=":id" element={<LegalNoticePage />} />
          </Route> */}
          <Route path={ROUTES.SCANNED_COPY} element={<ScannedCopyLayout />}>
            <Route index element={<ScannedCopyUploadPage />} />
            <Route path="options" element={<ScannedCopyOptionsPage />} />
            <Route path="preview" element={<ScannedCopyPreviewPage />} />
            <Route path="preview/grid" element={<ScannedCopyPreviewPage />} />
            <Route path="preview/split" element={<ScannedCopyPreviewPage />} />
            <Route path=":id/options" element={<ScannedCopyOptionsPage />} />
            <Route path=":id/preview" element={<ScannedCopyPreviewPage />} />
            <Route path=":id/preview/grid" element={<ScannedCopyPreviewPage />} />
            <Route path=":id/preview/split" element={<ScannedCopyPreviewPage />} />
          </Route>
          <Route path={ROUTES.TRANSLATOR} element={<TranslatorLayout />}>
            <Route index element={<TranslatorUploadPage />} />
            <Route path="options" element={<TranslatorOptionsPage />} />
            <Route path="preview" element={<TranslatorPreviewPage />} />
            <Route path=":id/options" element={<TranslatorOptionsPage />} />
            <Route path=":id/preview" element={<TranslatorPreviewPage />} />
          </Route>
          {/* <Route path={ROUTES.DOCUMENT_DRAFTING} element={<FastDraft />} />
          <Route path={ROUTES.SUBSCRIBE_CASES} element={<SubscribeCases />} />
          <Route path="/subscribed-cases/:caseId" element={<CaseDetailPage />} /> */}
          {/* <Route path={ROUTES.JUDGEMENT_FINDER} element={<JudgementFinder />} /> */}
          {/* <Route path={ROUTES.CALENDAR} element={<MyCalendar />} /> */}
          {/* Document Review Routes */}
          {/* <Route path={ROUTES.DOCUMENT_REVIEW} element={<DocumentReviewUploadLayout />}>
            <Route index element={<DocumentReviewUploadPage />} />
          </Route>
          <Route path={`${ROUTES.DOCUMENT_REVIEW}/:id`} element={<DocumentReviewLayout />}>
            <Route index element={<Navigate to="options" replace />} />
            <Route path="options" element={<DocumentReviewOptionsPage />} />
            <Route path="overall/results" element={<DocumentReviewResultsPage />} />
            <Route path="personalized/questions" element={<DocumentReviewQuestionsPage />} />
            <Route path="personalized/results" element={<DocumentReviewResultsPage />} />
          </Route> */}
          {/* <Route path={ROUTES.CUSTOM_DRAFT} element={<CustomDraft />} />
          <Route path={ROUTES.DRAFT_PRO} element={<DraftPro />} />{" "} */}
          {/* Base DraftPro route */}
          {/* <Route
            path={`${ROUTES.DRAFT_PRO}/extract-sections`}
            element={<DraftPro />}
          />{" "} */}
          {/* DraftPro extract sections route */}
          {/* <Route
            path={`${ROUTES.DRAFT_PRO}/generate-draft`}
            element={<DraftPro />}
          />{" "} */}
          {/* DraftPro generate draft route */}
          {/* <Route
            path={`${ROUTES.DRAFT_PRO}/:draftProId`}
            element={<DraftPro />}
          />{" "} */}
          {/* DraftPro route with ID */}
          {/* <Route
            path={ROUTES.CHOOSE_DRAFTING_METHOD}
            element={<ChooseDraftingMethod />}
          /> */}
          <Route path={ROUTES.USER_WALLET} element={<WalletPage />} />
          <Route path={ROUTES.LEGAL_VAULT} element={<LegalVault />} />
          <Route path={ROUTES.DISCLAIMER} element={<Disclaimer />} />
          <Route path={ROUTES.NOTIFICATIONS} element={<NotificationsPage />} />
          <Route path={ROUTES.SETTINGS} element={<SettingsPage />} />
          <Route path={ROUTES.PDF_TOOLS} element={<PdfToolsLayout />} />
          <Route path={ROUTES.PAGINATE} element={<PdfPaginate />} />
          <Route path={ROUTES.ERASE} element={<PdfErase />} />
          <Route path={ROUTES.MERGE_PDF} element={<PdfMerge />} />
          <Route path={ROUTES.REMOVE_PAGES} element={<PdfRemovePages />} />
          {/* <Route path={ROUTES.LINKEDIN_POST_GENERATOR}
            element={<LinkedInPostGenerator />}
          />{" "} */}
          {/* <Route path={ROUTES.AUDIO_TRANSCRIPTION} element={<AudioTranscriptionLayout />}>
            <Route index element={<AudioTranscriptionUploadPage />} />
            <Route path="language" element={<AudioTranscriptionLanguageSelectionPage />} />
            <Route path="transcript" element={<AudioTranscriptionTranscriptPage />} />
          </Route> */}
          <Route path={ROUTES.PRICING} element={<PricingPage />} />
          <Route path={ROUTES.ORG_DASHBOARD} element={<OrganizationDashboard />} />
          <Route
            path={ROUTES.USER_PROFILE_COMPLETION}
            element={<UserProfileCompletion />}
          />
          <Route path={`${ROUTES.FEEDBACK}/*`} element={<FeedbackPage />} />
          {/* 404 catch-all for authenticated users - inside Layout */}
          <Route path="*" element={<NotFound />} />
        </Route>
        <Route
          path={ROUTES.USER_PROFILE_COMPLETION}
          element={<UserProfileCompletion />}
        />
      </Route>
      {/* Catch-all for unauthenticated users */}
      <Route
        path="*"
        element={<Navigate to={ROUTES.LOGIN} />}
      />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
